"use client";

import { AlertTriangle, BedDouble, HeartPulse, ShieldCheck } from "lucide-react";
import { patient } from "@/src/mocks/patient";
import { useLang, localizedName } from "@/src/i18n";

type PatientBannerProps = {
  takeOrder: boolean;
  responsibleDoctor: string;
};

export function PatientBanner({ takeOrder, responsibleDoctor }: PatientBannerProps) {
  const { t, lang } = useLang();
  return (
    <section className="patient-banner" aria-label="Patient summary">
      <div className="patient-identity">
        <div>
          <div className="eyebrow">
            {patient.hn} <span>•</span> AN {patient.an}
          </div>
          <div className="patient-name-row">
            <h1>{localizedName(lang, patient.name, patient.englishName)}</h1>
            <span className="age-pill">{patient.age}Y · {patient.sex}</span>
          </div>
          <div className="patient-subline">
            <span><BedDouble size={14} /> {patient.ward} · {t("ward.bed")} {patient.bed}</span>
            <span><ShieldCheck size={14} /> {patient.coverage}</span>
          </div>
        </div>
      </div>

      <div className="patient-clinical">
        <div className="allergy-chip">
          <AlertTriangle size={14} />
          {t("pb.allergy")}: {patient.allergies[0]}
        </div>
        <div className="diagnosis-row">
          {patient.diagnoses.map((diagnosis) => (
            <span className="diagnosis-chip" key={diagnosis.code} title={diagnosis.label}>
              {diagnosis.shortLabel}
            </span>
          ))}
        </div>
      </div>

      <div className="mini-stats">
        <div className="mini-stat">
          <span>BP</span>
          <strong>{patient.vitals.bp}</strong>
        </div>
        <div className="mini-stat">
          <span>SpO₂</span>
          <strong>{patient.vitals.spo2}</strong>
        </div>
        <div className="live-chip"><HeartPulse size={14} /> {t("pb.live")}</div>
      </div>

      {takeOrder && (
        <div className="take-order-banner" role="status">
          {t("pb.takeOrder.pre")} {responsibleDoctor} — {t("pb.takeOrder.post")}
        </div>
      )}
    </section>
  );
}
